/**
 *  @fileOverview Calculator module
 *
 *  @author       Peter Schmiz <peter.schmiz@possible.com>
 *
 *  @requires     NPM:lodash
 *  @requires     NPM:detect-it
 */

import throttle from 'lodash/throttle';
import detectIt from 'detect-it';

const ENABLED_KEYS = [8, 9, 36, 37, 38, 39, 40, 46];
const SALARY_LIMIT = 20600;
const CONTRIBUTION_CAP = 140000;
const MAX_WORKING_HOURS = 35;

class Calculator {
  constructor() {
    this.isInputVisible = true;
    this.isDragActive = false;
    this.currentX = 0;
    this.dragEndX = 0;
    this.name = 'salary';
    this.actualWorkHours = 0;
  }

  init() {
    this.initDOMElements();
    if (this.calculator) {
      this.inFocus = false;
      this.parseInput(this.name);

      this.salaryLimit = parseInt(this.calculator.getAttribute('data-hsbc-salary-limit') || SALARY_LIMIT, 10);
      this.capLimit = parseInt(this.calculator.getAttribute('data-hsbc-cap-limit') || CONTRIBUTION_CAP, 10);
      this.maxWorkHours = parseInt(this.calculator.getAttribute('data-hsbc-max-working-hours') || MAX_WORKING_HOURS, 10);
      this.actualWorkHours = this.maxWorkHours;

      this.formattedSalaryLimit = this.salaryLimit.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,').slice(0, -3);
      this.formattedCapLimit = this.capLimit.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,').slice(0, -3);

      this.hsbcContribution = parseInt(this.calculator.getAttribute('data-hsbc-contribution') || 10, 10);
      this.hsbcContributionMod = this.hsbcContribution;
      this.hsbcContributionLimited = parseInt(this.calculator.getAttribute('data-hsbc-contribution-limit') || 9, 10);
      this.hsbcContributionText =
        this.calculator.getAttribute('data-hsbc-contribution-text') ||
        `(${this.hsbcContribution}% of first £${this.formattedSalaryLimit} and ${this.hsbcContributionLimited}% after that, capped at £${this.formattedSalaryLimit} up to the scheme earnings cap, currently £${this.formattedCapLimit})`;
      this.hsbcMatch = parseInt(this.calculator.getAttribute('data-hsbc-match') || 7, 10);
      this.contributionValue = parseInt(this.calculatorSlider.getAttribute('data-calculator-contribution') || 0, 10);
      this.updateSlider(this.contributionValue / this.calculatorMax);
      this.currentX = (this.contributionValue / this.calculatorMax) * this.sliderWidth;
      this.dragEndX = this.currentX;
      this.initBindings();
    }
  }

  initDOMElements() {
    this.calculator = document.body.querySelector('[data-calculator]');
    this.sliderValue = document.body.querySelector('#slider-value');
    if (this.calculator !== null) {
      this.calculatorShowLabelText = this.calculator.getAttribute('data-calculator-show-label') || 'Show';
      this.calculatorHideLabelText = this.calculator.getAttribute('data-calculator-hide-label') || 'Hide';
      this.calculatorYourContributionInput = this.calculator.querySelector('[data-your-contribution]');
      this.calculatorWorkingHoursInput = this.calculator.querySelector('[data-working-hours]');
      this.calculatorHide = this.calculator.querySelector('[data-calculator-hide]');
      this.calculatorSlider = this.calculator.querySelector('[data-calculator-slider]');
      this.calculatorHsbcContributionVal = this.calculator.querySelector('[data-hsbc-contribution-val]');
      this.calculatorHsbcContributionResult = this.calculator.querySelector('[data-hsbc-contribution-result]');
      this.calculatorYourContributionResult = this.calculator.querySelector('[data-your-contribution-result]');
      this.calculatorYourContributionValue = this.calculator.querySelector('[data-your-contribution-val]');
      this.calculatorHsbcMatchValue = this.calculator.querySelector('[data-hsbc-match-val]');
      this.calculatorHsbcMatchResult = this.calculator.querySelector('[data-hsbc-match-result]');
      this.calculatorOverallPercentage = this.calculator.querySelector('[data-overall-percentage]');
      this.calculatorOverallResult = this.calculator.querySelector('[data-overall-result]');

      if (this.calculatorHide !== null) {
        this.calculatorHideLabel = this.calculatorHide.querySelector('[data-calculator-hide-label]');
      }

      if (this.calculatorSlider !== null) {
        this.sliderWidth = this.calculatorSlider.offsetWidth;
        this.calculatorBullet = this.calculatorSlider.querySelector('[data-calculator-bullet]');
        this.calculatorBar = this.calculatorSlider.querySelector('[data-calculator-bar]');
        this.calculatorAxis = this.calculatorSlider.querySelector('[data-calculator-axis]');

        if (this.calculatorAxis !== null) {
          this.calculatorMax = parseInt(this.calculatorAxis.getAttribute('data-calculator-max') || 30, 10);
        }
      }
    }
  }

  initBindings() {
    this.calculatorHide.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.isInputVisible) {
        this.updateInput(this.name);
      }
      this.toggleMask();
    }, false);

    this.calculatorYourContributionInput.addEventListener('keydown', (e) => {
      this.evaluateKeyEvent(e, e.currentTarget);
    }, false);

    this.calculatorWorkingHoursInput.addEventListener('keydown', (e) => {
      this.evaluateKeyEvent(e, e.currentTarget);
    }, false);

    this.calculatorYourContributionInput.addEventListener('keyup', (e) => {
      if (this.calculatorYourContributionInput.value > this.capLimit) {
        this.calculatorYourContributionInput.value = this.capLimit;
      }
      if (this.calculatorYourContributionInput.value.length > 6) {
        this.calculatorYourContributionInput.value = this.salaryValue;
        return false;
      }
      if (this.isInputVisible === true) {
        const name = e.currentTarget.getAttribute('name');
        this.updateInput(name);
        this.doCalculation();
      }
      return true;
    }, false);

    this.calculatorWorkingHoursInput.addEventListener('keyup', (e) => {
      const name = e.currentTarget.getAttribute('name');
      if (this.calculatorWorkingHoursInput.value > this.maxWorkHours) {
        this.calculatorWorkingHoursInput.value = this.maxWorkHours;
      }
      if (this.isInputVisible === true) {
        this.updateInput(name);
        this.doCalculation();
      }
      return true;
    }, false);

    this.calculatorSlider.addEventListener('click', (e) => {
      e.preventDefault();
      const rect = e.target.getBoundingClientRect();
      const posX = e.pageX - rect.left;
      this.currentX = posX;
      this.dragEndX = this.currentX;
      this.updateSlider(posX / this.sliderWidth);
    }, false);

    this.calculatorBullet.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    }, false);

    this.calculatorBullet.addEventListener('mousedown', (e) => {
      this.onStart(e);
    }, detectIt.passiveEvents && this.isDragActive === false ? {
      capture: false,
      passive: true,
    } : false);

    this.calculatorBullet.addEventListener('touchstart', (e) => {
      this.onStart(e);
    }, detectIt.passiveEvents ? {
      capture: false,
      passive: true,
    } : false);

    document.body.addEventListener('mouseup', (e) => {
      this.onEnd(e);
    }, false);

    document.body.addEventListener('touchstart', (e) => {
      if (this.inFocus === true) {
        this.onStart(e);
      }
    }, false);

    document.body.addEventListener('touchend', (e) => {
      this.onEnd(e);
    }, false);

    this.calculatorBullet.addEventListener('focus', () => {
      this.inFocus = true;
    }, false);

    this.calculatorBullet.addEventListener('blur', () => {
      this.isDragActive = false;
      this.inFocus = false;
    }, false);

    this.calculator.addEventListener('mousemove', (e) => {
      this.onMove(e);
    }, false);

    this.calculator.addEventListener('touchmove', (e) => {
      this.onMove(e);
    }, false);

    window.addEventListener('resize', throttle(() => {
      this.sliderWidth = this.calculatorSlider.offsetWidth;
    }, 50), detectIt.passiveEvents ? { capture: false, passive: true } : false);

    document.body.addEventListener('keydown', (e) => {
      this.cursorStep(e);
    }, false);
  }

  evaluateKeyEvent(event, target) {
    const inputName = target.getAttribute('name');
    if (
      (event.keyCode < 96 || event.keyCode > 105) &&
      (event.shiftKey === true || (event.keyCode < 48 || event.keyCode > 57)) &&
      ((event.shiftKey === true || event.keyCode !== 190) || inputName.toLowerCase() !== 'working-hours') &&
      (ENABLED_KEYS.includes(event.keyCode) === false || this.isInputVisible === false)
    ) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  cursorStep(e) {
    if (
      (e.keyCode === 37 || e.keyCode === 39) &&
      this.calculatorBullet === document.activeElement
    ) {
      const currentPosition = this.currentX / this.sliderWidth;
      const step = (1 / 30) * (e.keyCode === 37 ? -1 : 1);

      this.currentX += this.sliderWidth * step;

      if (this.currentX < 0) {
        this.currentX = 0;
      }

      if (this.currentX > this.sliderWidth) {
        this.currentX = this.sliderWidth;
      }

      this.updateSlider(currentPosition + step);
      e.preventDefault();
      return false;
    }
    return true;
  }

  onStart(e) {
    this.isDragActive = true;
    this.calculator.classList.add('calculator--drag-active');
    this.dragStartX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
  }

  onMove(e) {
    if (this.isDragActive) {
      e.preventDefault();
      this.handleDrag(e);
    }
  }

  onEnd() {
    if (this.isDragActive) {
      this.calculator.classList.remove('calculator--drag-active');
      this.isDragActive = false;
      this.dragEndX = this.currentX;
      this.inFocus = false;
    }
  }

  updateInput(name) {
    this.parseInput(name);
    if (this.calculatorYourContributionInput.value.charAt(0) === '0') {
      this.calculatorYourContributionInput.value = this.salaryValue;
    }
  }

  toggleMask() {
    this.isInputVisible = !this.isInputVisible;
    if (this.isInputVisible) {
      this.calculatorYourContributionInput.classList.remove('hidden');
      this.calculatorYourContributionInput.type = 'number';
      this.calculatorYourContributionInput.readOnly = false;
      this.calculatorYourContributionInput.value = parseInt(this.salaryValue, 10);
      this.calculatorHide.classList.remove('hide-button--active');
      this.calculatorHide.setAttribute('aria-label', 'Hide field value');
      this.calculatorHideLabel.textContent = this.calculatorHideLabelText;
    } else {
      this.calculatorYourContributionInput.classList.add('hidden');
      this.calculatorYourContributionInput.type = 'text';
      this.calculatorYourContributionInput.readOnly = true;
      this.calculatorYourContributionInput.value = '*'.repeat(this.salaryValue.toString().length);
      this.calculatorHide.classList.add('hide-button--active');
      this.calculatorHideLabel.textContent = this.calculatorShowLabelText;
      this.calculatorHide.setAttribute('aria-label', 'Show field value');
    }
  }

  parseInput(name) {
    if (name === 'salary') {
      this.salaryValue = parseInt(this.calculatorYourContributionInput.value || 0, 10);
    }
    if (name === 'working-hours') {
      this.actualWorkHours = parseFloat(
        this.calculatorWorkingHoursInput.value ||
        this.maxWorkHours, 10
      );
    }
  }

  doCalculation() {
    const contributedSalary = ((this.salaryValue / 100) * this.contributionValue) / 12;
    const hsbcContributionTxt = `<span class="calculator__result-label--additional">${this.hsbcContributionText}</span>`;

    let hsbcContributionResult = 0;
    let hsbcContributionDiff = 0;

    if (this.salaryValue <= this.salaryLimit) {
      hsbcContributionResult = (
        ((this.actualWorkHours / this.maxWorkHours) * (this.salaryValue / 100))
        * this.hsbcContribution
      ) / 12;
    } else {
      hsbcContributionResult = (
        (((this.actualWorkHours / this.maxWorkHours) * this.salaryLimit) / 100)
        * this.hsbcContribution
      ) / 12;
      hsbcContributionDiff = this.salaryValue - (
        (this.actualWorkHours / this.maxWorkHours) * this.salaryLimit
      );

      if (hsbcContributionDiff > this.capLimit) {
        hsbcContributionDiff = this.capLimit;
      }
      hsbcContributionResult += ((hsbcContributionDiff / 100) * this.hsbcContributionLimited) / 12;
    }

    let hsbcMatchResult = 0;

    if (this.contributionValue <= this.hsbcMatch) {
      hsbcMatchResult = contributedSalary;
    } else {
      hsbcMatchResult = ((this.salaryValue / 100) * this.hsbcMatch) / 12;
    }

    const overallResult = (contributedSalary + hsbcContributionResult + hsbcMatchResult);

    if (contributedSalary > 0) {
      this.hsbcContributionMod = Math.round(
        (((hsbcContributionResult * 12) / this.salaryValue) * 100) * 100
      ) / 100;
    }

    this.calculatorHsbcContributionVal.innerHTML = `${this.salaryValue <= this.salaryLimit ? `${this.hsbcContribution}%`
      : `${this.hsbcContributionMod}%`} ${hsbcContributionTxt}`;
    this.calculatorHsbcContributionResult.innerHTML = this.formattedResult(hsbcContributionResult);
    this.calculatorHsbcMatchValue.innerHTML = `${this.contributionValue < this.hsbcMatch ? this.contributionValue
      : this.hsbcMatch}%`;
    this.calculatorHsbcMatchResult.innerHTML = this.formattedResult(hsbcMatchResult);
    this.calculatorOverallResult.innerHTML = this.formattedResult(overallResult);
    this.calculatorYourContributionValue.innerHTML = `${this.contributionValue}%`;
    this.calculatorYourContributionResult.innerHTML = this.formattedResult(contributedSalary);
    this.calculatorOverallPercentage.innerHTML = `${Math.round(
      (
        this.contributionValue + (this.salaryValue <= this.salaryLimit ? this.hsbcContribution
          : this.hsbcContributionMod) + (this.contributionValue < this.hsbcMatch
          ? this.contributionValue : this.hsbcMatch)
      ) * 100
    ) / 100}%`;
  }

  handleDrag(e) {
    const distance = (this.dragEndX || 0) + ((e.type === 'mousemove' ? e.clientX
      : e.touches[0].clientX) - this.dragStartX);
    const percentageX = distance > 0 ? distance / this.sliderWidth : 0;

    this.currentX = distance;

    if (this.currentX < 0) {
      this.currentX = 0;
    }

    if (this.currentX > this.sliderWidth) {
      this.currentX = this.sliderWidth;
    }

    this.updateSlider(percentageX);
  }

  updateSlider(percentageX) {
    let xPos = percentageX;

    if (xPos < 0) {
      xPos = 0;
    }

    if (xPos > 1) {
      xPos = 1;
    }

    const labelPercentage = Math.round((this.calculatorMax / 100) * Math.round(xPos * 100));
    const stylePercentage = Math.round(xPos * 100);

    this.contributionValue = labelPercentage;
    this.calculatorBullet.setAttribute('data-contribution-percent', `${labelPercentage}%`);
    this.calculatorSlider.setAttribute('aria-valuetext', `${labelPercentage}%`);
    this.calculatorSlider.setAttribute('aria-valuenow', labelPercentage);
    this.calculatorBullet.style.left = `${stylePercentage}%`;
    this.calculatorBar.style.width = `${stylePercentage}%`;
    this.sliderValue.innerHTML = `${labelPercentage}%`;

    this.doCalculation();
  }

  formattedResult(number) {
	  let lastTwoDigit = number.toFixed(2);
	  lastTwoDigit = (lastTwoDigit + "").split(".")[1];
      return `${Math.floor(number)}.<span class="calculator__result-value--small">${lastTwoDigit}</span>`;
  }
}

export const calculator = new Calculator();



// WEBPACK FOOTER //
// ./src/js/modules/calculator.js